import React, { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import FormControl from '@mui/material/FormControl';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import FormHelperText from '@mui/material/FormHelperText';
import Switch from '@mui/material/Switch';

import { StyledFormControlLabel } from './styles';

export const VpHookFormSwitch = ({
  name = '',
  defaultValue = false,
  required = false,
  rules = {},
  disabled = false,
  onChange = null,
  label = '',
  helperText = '',
  disableHelperText = true,
  labelPlacement = 'start',
  includeDisplayInput = true,
  ...rest
}) => {
  const id = `vp-hook-switch-${name}`;
  const displayInputName = `${name}-display`;

  // prevents issues where defaultValue is null
  const defaultInputValue = defaultValue || false;
  const { setValue, register } = useFormContext();
  const {
    field: { onChange: onControllerChange, onBlur, value, name: inputName, ref },
    fieldState: { error }
  } = useController({
    name: includeDisplayInput ? displayInputName : name,
    rules: { required, ...rules },
    defaultValue: defaultInputValue,
    shouldUnregister: true
  });

  useEffect(() => {
    if (includeDisplayInput) {
      setValue(name, value);
    }
  }, [value]);

  useEffect(() => {
    if (includeDisplayInput) {
      setValue(displayInputName, defaultInputValue);
    }
    setValue(name, defaultInputValue);
  }, [defaultInputValue]);

  // helper text spacing will always be there so we don't "push down" elements
  const errorMessage = error?.message;
  const defaultHelperText = disableHelperText ? '' : <span>&nbsp;</span>;
  const helperTextMessage = errorMessage || helperText || defaultHelperText;

  const onSelectChange = (e) => {
    onControllerChange(e);
    onChange && onChange(e);
  };

  return (
    <FormControl fullWidth variant="outlined">
      <StyledFormControlLabel
        control={
          <Switch
            inputRef={ref}
            id={id}
            onChange={onSelectChange}
            onBlur={onBlur}
            checked={value}
            name={inputName}
            disabled={disabled}
            {...rest}
          />
        }
        labelPlacement={labelPlacement}
        label={<Typography variant="switchLabel">{label}</Typography>}
      />
      {!disableHelperText && (
        <FormHelperText error={!!error} id={`${id}-helper-text`}>
          {helperTextMessage}
        </FormHelperText>
      )}
      {/* Disabling the switch makes the react-hook-form value undefined, 
      so creating hidden input with real value for that case */}
      <input
        {...register(name)}
        type="checkbox"
        style={{ display: 'none' }}
        defaultChecked={defaultValue}
      />
    </FormControl>
  );
};

VpHookFormSwitch.propTypes = {
  name: PropTypes.string.isRequired,
  defaultValue: PropTypes.bool,
  required: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  rules: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  disableHelperText: PropTypes.string,
  labelPlacement: PropTypes.string,
  includeDisplayInput: PropTypes.bool
};

export default VpHookFormSwitch;



// WEBPACK FOOTER //
// ./src/vp-ui/inputs/VpHookFormSwitch/VpHookFormSwitch.jsx